// OnboardingFlow - Scanning → Location Found → Get Ready Transition → Diagnostic
// Premium UI with glassmorphism, hero images, and smooth transitions

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, ArrowRight, MapPin, CheckCircle2, TrendingUp, TrendingDown, PoundSterling, Zap, Info } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ThemeToggle, useTheme } from "@/components/ThemeProvider";
import { feedback } from "@/utils/feedback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero images for backgrounds
const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/x6hz075y_edensdad._Refined_cross-sectional_architectural_photo_showing_i_34466e4d-a639-432a-823e-dfadbf330e4c.png",
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/bjy1lw5k_edensdad._Refined_cross-sectional_architectural_photo_showing_i_cf1302c0-53c3-4bc7-9b79-82907914deed.png",
  "https://customer-assets.emergentagent.com/job_61f9b4e5-9795-47b1-942b-e3ab1711fd8f/artifacts/jiw8ixlc_edensdad._cinematic_close-up_cross-view_of_luxury_British_home__aa4984b8-4df0-4c41-b9bc-f7261784d14d.png"
];

// Dynamic scanning texts
const SCANNING_TEXTS = [
  "Locating property context…",
  "Analysing spatial patterns…",
  "Cross-checking local signals…",
  "Mapping category behaviour…",
  "Applying integrity filters…",
  "Property located"
];

// Market insight explanations - framed as direct statements
const MARKET_EXPLANATIONS = {
  avg_price: "Average sale price locally over 12 months",
  price_change: (change) => `Property values changed by ${change >= 0 ? '+' : ''}${change?.toFixed(1) || 0}% in last year`,
  rental_yield: "Typical annual rental return locally"
};

// ============== Matrix Data Rain ==============
function MatrixRain() {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    const chars = ['£', '%', 'm²', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '↑', '↓', 'SO', 'SW', 'W1', 'SE', 'NW', '£k', '.5'];
    const fontSize = 14;
    let columns = 0;
    let drops = [];
    
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
      columns = Math.floor(window.innerWidth / fontSize);
      drops = Array(columns).fill(0).map(() => Math.random() * -50);
    };
    resize();
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      
      ctx.fillStyle = 'rgba(0, 106, 77, 0.12)';
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < columns; i++) {
        const char = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        
        if (y > 0) {
          ctx.fillStyle = `rgba(0, 106, 77, ${0.05 + Math.random() * 0.1})`;
          ctx.fillText(char, x, y);
        }
        
        drops[i] += 0.3 + Math.random() * 0.3;
        if (drops[i] * fontSize > window.innerHeight && Math.random() > 0.98) {
          drops[i] = 0;
        }
      }
    };
    
    const interval = setInterval(draw, 50);
    window.addEventListener('resize', resize);
    return () => { clearInterval(interval); window.removeEventListener('resize', resize); };
  }, []);
  
  return <canvas ref={canvasRef} className="absolute inset-0 z-[1] pointer-events-none" />;
}

// ============== Shimmer Loading Effect ==============
function ShimmerLoader({ className = "" }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </div>
  );
}

// ============== Market Insight Skeleton with Shimmer ==============
function MarketInsightSkeleton() {
  return (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-4 mb-4 shadow-xl">
      <div className="h-3 w-24 bg-white/10 rounded mb-4 relative overflow-hidden">
        <ShimmerLoader className="absolute inset-0" />
      </div>
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-white/10 rounded relative overflow-hidden">
                <ShimmerLoader className="absolute inset-0" />
              </div>
              <div>
                <div className="h-5 w-16 bg-white/10 rounded mb-1 relative overflow-hidden">
                  <ShimmerLoader className="absolute inset-0" />
                </div>
                <div className="h-3 w-12 bg-white/5 rounded relative overflow-hidden">
                  <ShimmerLoader className="absolute inset-0" />
                </div>
              </div>
            </div>
            <div className="h-8 w-32 bg-white/5 rounded relative overflow-hidden">
              <ShimmerLoader className="absolute inset-0" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== Scanning Reticle ==============
function ScanningReticle({ className = "", size = "normal" }) {
  const sizeClass = size === "small" ? "w-16 h-16" : "w-32 h-32";
  
  return (
    <div className={`relative ${sizeClass} ${className}`}>
      <div className="absolute inset-0 rounded-full border-2 border-cipher-green/20">
        <div className="absolute inset-0 rounded-full border-t-2 border-cipher-green/60 animate-spin" style={{ animationDuration: '3s' }} />
      </div>
      <div className="absolute inset-[15%] rounded-full border border-cipher-green/30 animate-pulse" />
      <div className="absolute inset-[30%] rounded-full bg-cipher-green/5 border border-cipher-green/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          <div className={`absolute bg-cipher-green/40 -left-1.5 top-1/2 -translate-y-1/2 ${size === "small" ? "w-3 h-0.5" : "w-6 h-0.5"}`} />
          <div className={`absolute bg-cipher-green/40 left-1/2 -translate-x-1/2 ${size === "small" ? "h-3 w-0.5 -top-1.5" : "h-6 w-0.5 -top-3"}`} />
          <div className={`rounded-full bg-cipher-green relative ${size === "small" ? "w-1.5 h-1.5" : "w-3 h-3"}`}>
            <div className="absolute inset-0 rounded-full bg-cipher-green animate-ping opacity-50" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ============== Location Marker ==============
function LocationMarker({ className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute inset-0 w-24 h-24 -ml-10 -mt-10 rounded-full border-2 border-cipher-green/30 animate-ping" />
      <div className="absolute inset-0 w-20 h-20 -ml-8 -mt-8 rounded-full border border-cipher-green/20 animate-pulse" />
      <div className="absolute inset-0 w-16 h-16 -ml-6 -mt-6 rounded-full bg-cipher-green/10" />
      <div className="relative w-4 h-4 bg-cipher-green rounded-full shadow-lg border-2 border-white">
        <div className="absolute inset-0 rounded-full bg-cipher-green animate-pulse" />
      </div>
    </div>
  );
}

// ============== Main Component ==============
export default function OnboardingFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const [step, setStep] = useState("scanning");
  const [sessionId, setSessionId] = useState(null);
  const [postcode, setPostcode] = useState("");
  const [scanData, setScanData] = useState(null);
  const [scanTextIndex, setScanTextIndex] = useState(0);
  const [mapboxToken, setMapboxToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transitionTextIndex, setTransitionTextIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  // Get postcode from navigation state
  useEffect(() => {
    const statePostcode = location.state?.postcode;
    if (!statePostcode) {
      navigate('/');
      return;
    }
    setPostcode(statePostcode);
  }, [location.state, navigate]);

  // Fetch mapbox token
  useEffect(() => {
    axios.get(`${API}/config/mapbox`).then(res => setMapboxToken(res.data.token)).catch(() => {});
  }, []);

  // Start the location fetch when we have a postcode
  useEffect(() => {
    if (!postcode) return;
    
    const initSession = async () => {
      try {
        const startTime = Date.now();
        const sessionRes = await axios.post(`${API}/cipher/start`, {});
        const newSessionId = sessionRes.data.session_id;
        setSessionId(newSessionId);
        
        const locationRes = await axios.post(`${API}/cipher/location`, {
          session_id: newSessionId,
          postcode: postcode,
          property_type: "classic"
        });
        
        sessionStorage.setItem(`cipher_session_${newSessionId}`, JSON.stringify({
          postcode: locationRes.data.postcode || postcode
        }));
        
        // Smart delay: ensure minimum 1.8s animation but don't add unnecessary wait
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1800 - elapsed);
        
        setTimeout(() => {
          setScanData(locationRes.data);
          setStep("found");
        }, remainingDelay);
        
      } catch (error) {
        toast.error("Could not locate property. Please check the postcode.");
        navigate('/');
      }
    };
    
    initSession();
  }, [postcode, navigate]);

  // Scanning text animation - cycle through texts in 2s
  useEffect(() => {
    if (step === "scanning") {
      const interval = setInterval(() => {
        setScanTextIndex(prev => prev < SCANNING_TEXTS.length - 1 ? prev + 1 : prev);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [step]);

  // Transition screen - auto-advance after 1.5-2 seconds
  useEffect(() => {
    if (step === "transition") {
      const timer1 = setTimeout(() => setTransitionTextIndex(1), 400);
      const timer2 = setTimeout(() => setTransitionTextIndex(2), 800);
      const fadeTimer = setTimeout(() => setFadeOut(true), 1400);
      const navTimer = setTimeout(() => {
        navigate(`/diagnostic/${sessionId}`);
      }, 1800);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(fadeTimer);
        clearTimeout(navTimer);
      };
    }
  }, [step, sessionId, navigate]);

  const handleProceed = async () => {
    setLoading(true);
    feedback.tap();
    
    try {
      setFadeOut(true);
      setTimeout(() => {
        setFadeOut(false);
        setStep("transition");
      }, 300);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Format price for display
  const formatPrice = (price) => {
    if (!price) return null;
    if (price >= 1000000) return `£${(price / 1000000).toFixed(2)}M`;
    return `£${(price / 1000).toFixed(0)}K`;
  };

  // Get market insights with explanations
  const getMarketInsights = () => {
    if (!scanData) return [];
    
    const insights = [];
    
    if (scanData.avg_price) {
      insights.push({
        icon: <PoundSterling className="w-4 h-4" />,
        label: "Avg. Value",
        value: formatPrice(scanData.avg_price),
        color: "text-white",
        explanation: MARKET_EXPLANATIONS.avg_price
      });
    }
    
    if (scanData.price_change_1y !== null && scanData.price_change_1y !== undefined) {
      const trend = scanData.price_change_1y >= 0;
      const Icon = trend ? TrendingUp : TrendingDown;
      insights.push({
        icon: <Icon className="w-4 h-4" />,
        label: "1Y Change",
        value: `${trend ? "+" : ""}${scanData.price_change_1y.toFixed(1)}%`,
        color: trend ? "text-cipher-green" : "text-red-400",
        explanation: MARKET_EXPLANATIONS.price_change(scanData.price_change_1y)
      });
    }
    
    if (scanData.rental_yield) {
      insights.push({
        icon: <Zap className="w-4 h-4" />,
        label: "Yield",
        value: `${scanData.rental_yield.toFixed(1)}%`,
        color: "text-cipher-gold",
        explanation: MARKET_EXPLANATIONS.rental_yield
      });
    }
    
    return insights;
  };

  // Get area statement - true factual statements about the area
  const getAreaStatement = () => {
    const areaName = getAreaName();
    const statements = [
      `${areaName} demonstrates consistent property activity with diverse housing stock.`,
      `${areaName} shows established market patterns with varied property types.`,
      `${areaName} features a mix of residential properties with observable market trends.`
    ];
    // Deterministic selection based on postcode
    const index = postcode.charCodeAt(0) % statements.length;
    return statements[index];
  };

  // Get area name for display
  const getAreaName = () => {
    if (scanData?.district) return scanData.district;
    if (scanData?.ward) return scanData.ward;
    return "your area";
  };

  // ============== SCANNING SCREEN (Frosted Glass + Matrix) ==============
  if (step === "scanning") {
    return (
      <div className={`fixed inset-0 z-50 flex flex-col overflow-hidden transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
        {/* Hero image background with frosted glass effect */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGES[1]}
            alt="Property"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 backdrop-blur-sm bg-black/50" />
        </div>
        
        {/* Matrix data rain */}
        <MatrixRain />
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
          {/* Glassmorphism card for scanning content */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl max-w-sm w-full">
            <div className="flex justify-center mb-6">
              <ScanningReticle />
            </div>
            
            <div className="text-center mb-6 h-16">
              <p className="text-lg font-medium text-white mb-2 animate-pulse">
                {SCANNING_TEXTS[scanTextIndex]}
              </p>
              <p className="text-sm text-white/60">{postcode}</p>
            </div>
            
            <div className="flex items-center justify-center gap-2">
              {SCANNING_TEXTS.map((_, i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-500 ${
                    i <= scanTextIndex ? "bg-cipher-green scale-100" : "bg-white/30 scale-75"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        
        <div className="relative z-10 pb-8 text-center">
          <p className="text-xs text-white/40">HiPPO by StudioNPG</p>
        </div>
      </div>
    );
  }

  // ============== GET READY TRANSITION SCREEN (Frosted Glass) ==============
  if (step === "transition") {
    return (
      <div className={`fixed inset-0 z-50 flex flex-col overflow-hidden transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
        {/* Hero image background with stronger frosted glass effect */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGES[2]}
            alt="Property"
            className="w-full h-full object-cover"
          />
          {/* Frosted glass overlay */}
          <div className="absolute inset-0 backdrop-blur-md bg-black/50" />
        </div>
        
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
          {/* Frosted glass card for transition content */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl max-w-sm w-full">
            <div className="flex justify-center mb-6">
              <div className="opacity-60">
                <ScanningReticle size="small" />
              </div>
            </div>
            
            <div className="text-center">
              <p className={`text-2xl font-bold text-white mb-3 transition-all duration-300 ${transitionTextIndex >= 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                Get ready
              </p>
              <p className={`text-base text-white/70 mb-2 transition-all duration-300 delay-100 ${transitionTextIndex >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                A few simple questions follow.
              </p>
              <p className={`text-sm text-white/50 transition-all duration-300 delay-200 ${transitionTextIndex >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                Estimates are fine.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== FOUND SCREEN ==============
  if (step === "found" && scanData) {
    const marketInsights = getMarketInsights();
    const areaName = getAreaName();
    
    return (
      <div className={`fixed inset-0 z-50 flex flex-col transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
        {/* Full-screen Map Background */}
        <div className="absolute inset-0">
          {mapboxToken && scanData.latitude && scanData.longitude && (
            <img
              src={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/${scanData.longitude},${scanData.latitude},15,0/800x1200@2x?access_token=${mapboxToken}`}
              alt="Property location"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
        </div>

        {/* No header — clean map experience */}

        {/* Center Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4">
          {/* Location Marker */}
          <LocationMarker />
          
          {/* Postcode Badge */}
          <div className="mt-4 flex items-center gap-2 bg-cipher-green text-white px-5 py-2.5 rounded-xl shadow-xl">
            <MapPin className="w-4 h-4" />
            <span className="font-bold text-lg">{scanData.postcode}</span>
          </div>
          
          {/* Site Found Badge */}
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white">
            <CheckCircle2 className="w-4 h-4 text-cipher-green" />
            <span className="text-sm font-medium">Property Found</span>
          </div>
          
          <h2 className="mt-3 text-2xl font-bold text-white">{areaName}</h2>
          
          {/* Area Statement */}
          <p className="mt-2 text-sm text-white/60 text-center max-w-xs">
            We've found local data for <span className="text-white/80 font-medium">{scanData.postcode}</span>
          </p>
        </div>

        {/* Bottom Panel */}
        <div className="relative z-10 p-4 pb-8">
          {/* Simplified market insight — one key stat or fallback */}
          {scanData.avg_price ? (
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-4 mb-4 shadow-xl">
              <h3 className="text-xs font-semibold text-cipher-green uppercase tracking-wider mb-2">
                Local Market
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <PoundSterling className="w-4 h-4 text-white/50" />
                  <div>
                    <p className="text-lg font-bold text-white">{formatPrice(scanData.avg_price)}</p>
                    <p className="text-xs text-white/40">Avg. local value</p>
                  </div>
                </div>
                {scanData.price_change_1y !== null && scanData.price_change_1y !== undefined && (
                  <div className="text-right">
                    <p className={`text-sm font-semibold ${scanData.price_change_1y >= 0 ? 'text-cipher-green' : 'text-red-400'}`}>
                      {scanData.price_change_1y >= 0 ? '+' : ''}{scanData.price_change_1y.toFixed(1)}%
                    </p>
                    <p className="text-[10px] text-white/30">1Y change</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-4 mb-4 shadow-xl">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-cipher-green" />
                <p className="text-sm text-white/70">Local context available — let's analyse your property</p>
              </div>
            </div>
          )}
          
          {/* How we're different */}
          <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-3 mb-4" data-testid="how-different">
            <p className="text-xs text-white/60 text-center leading-relaxed">
              Unlike valuations, HiPPO shows you what to change <span className="text-white/80 font-medium">inside your home</span> to unlock trapped value — no extensions, no guesswork.
            </p>
          </div>
          
          {/* Statement */}
          <p className="text-sm text-white/70 text-center mb-4">
            A few simple questions will generate your personalised intelligence.
          </p>
          
          {/* Continue Button — specific CTA */}
          <Button
            onClick={handleProceed}
            className="w-full bg-cipher-green hover:bg-cipher-green-light text-white rounded-xl py-4 font-medium shadow-lg shadow-cipher-green/30 hover:shadow-xl hover:scale-[1.01] transition-all"
            disabled={loading}
            data-testid="proceed-btn"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Analyse My Property
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          
          <p className="text-xs text-white/40 text-center mt-4">
            Observational signals — Not financial advice
          </p>
        </div>
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className={`min-h-screen flex items-center justify-center transition-colors ${
      isDark ? 'bg-cipher-charcoal' : 'bg-cipher-bone'
    }`}>
      <div className="w-8 h-8 border-2 border-cipher-green/30 border-t-cipher-green rounded-full animate-spin" />
    </div>
  );
}
